export interface CameraScore {
    device: MediaDeviceInfo
    label: string
    score: number
    deviceId: string
}

export function useCameraSelector() {
    const isBackLabel = (label: string): boolean => {
        const l = (label || '').toLowerCase()
        const patterns = [
            'back',
            'rear',
            'environment',
            'зад',
            'задняя',
            'тыльн',
            'основн',
            'тилова',
            'основна',
            'trasera',
            'posterior',
            'traseira',
            'rück',
            'hinten',
            'arrière',
            'dos',
            'posteriore',
            'retro',
            'tylna',
            'tył',
            'arka',
            'אחור',
            'خلف',
            '后置',
            '後置',
            '后摄',
            '後攝',
            '背面',
            'リア',
            '후면',
            'sau',
            'หลัง',
            'belakang',
        ]
        return patterns.some((p) => l.includes(p))
    }

    const isFrontLabel = (label: string): boolean => {
        const l = (label || '').toLowerCase()
        const patterns = [
            'front',
            'user',
            'selfie',
            'перед',
            'передняя',
            'фронт',
            'фронталь',
            'селфи',
            'селфі',
            'frontal',
            'delantera',
            'vorder',
            'vorne',
            'front',
            'avant',
            'frontal',
            'anteriore',
            'frontale',
            'przednia',
            'ön',
            'קדמית',
            'أمامي',
            '前置',
            '前摄',
            '前面',
            'フロント',
            '전면',
            'trước',
            'หน้า',
            'depan',
        ]
        return patterns.some((p) => l.includes(p))
    }

    const selectBestCameraByScore = (devices: MediaDeviceInfo[]): string | null => {
        const videoDevices = devices.filter((d) => d.kind === 'videoinput')
        if (videoDevices.length === 0) return null

        // Жёстко исключаем фронтальные камеры из кандидатов, если есть хоть одна не-фронтальная
        const nonFrontDevices = videoDevices.filter((d) => !isFrontLabel(d.label))
        const candidates = nonFrontDevices.length > 0 ? nonFrontDevices : videoDevices

        // Эвристика по лейблам (и MP), без capabilities
        const hasBack = candidates.some((d) => isBackLabel(d.label))
        const scoredHeuristic = candidates.map((d, idx) => {
            let score = 0
            const l = (d.label || '').toLowerCase()
            // Задняя/передняя
            if (isBackLabel(l)) score += 50
            if (isFrontLabel(l)) score -= 30
            // Основная (без ultra): wide/main/standard/dual wide
            const isUltraWide = [
                'ultra wide',
                'ultrawide',
                'ultra-wide',
                'ultraweit',
                'ultraweitwinkel',
                'ultra grand angle',
                'ultra grand-angle',
                'ultra grande angular',
                'сверхширок',
                '超广角',
                '超廣角',
                '초광각',
                '超広角',
            ].some((p) => l.includes(p))
            const isWide = [
                'wide',
                'weitwinkel',
                'grand angle',
                'grande angular',
                'grandangolare',
                'широкоуголь',
                '広角',
                '광각',
            ].some((p) => l.includes(p))
            const isDualWide =
                [
                    'dual wide',
                    'dual grand angle',
                    'dual grande angular',
                    'dual wide camera',
                    'двойн',
                ].some((p) => l.includes(p)) &&
                isWide &&
                !isUltraWide
            if (isWide && !isUltraWide) score += 25
            if (isDualWide) score += 15
            // Нежелательные линзы
            const isMacro = ['macro', 'макро', 'マクロ', '매크로'].some((p) => l.includes(p))
            const isDepthLike = [
                'depth',
                'bokeh',
                'portrait',
                'tof',
                'lidar',
                'mono',
                'глубин',
            ].some((p) => l.includes(p))
            const isTele = [
                'tele',
                'telephoto',
                'телеобъектив',
                '望遠',
                '망원',
                '长焦',
                '長焦',
            ].some((p) => l.includes(p))
            if (isUltraWide) score -= 50
            if (isMacro) score -= 35
            if (isDepthLike) score -= 35
            if (isTele) score -= 15
            // Числовые маркеры увеличения
            const zoomMatch = l.match(/(\d+(?:\.\d+)?)\s*x/)
            if (zoomMatch) {
                const x = parseFloat(zoomMatch[1])
                if (!Number.isNaN(x)) {
                    if (x === 1) score += 20
                    else if (x <= 0.6) score -= 40
                    else if (x >= 2) score -= Math.min(20, x * 5)
                }
            }
            // Android-паттерн основной камеры
            if ((l.includes('camera2 0') || l.includes('camera 0')) && isBackLabel(l)) {
                score += 30
            }

            const mpMatch = d.label.match(/\b([0-9]+)\s*MP?\b/i)
            if (mpMatch) {
                const mp = parseInt(mpMatch[1], 10)
                if (!Number.isNaN(mp)) score += Math.min(mp, 64)
            }

            // Если нет явной задней камеры по label, усиливаем последнюю как вероятную back
            if (!hasBack && idx === candidates.length - 1) score += 20

            return { device: d, label: d.label, score, deviceId: d.deviceId }
        })

        scoredHeuristic.sort((a, b) => b.score - a.score)
        return scoredHeuristic[0]?.deviceId || candidates[candidates.length - 1]?.deviceId || null
    }
    return {
        selectBestCameraByScore,
    }
}
